import useNotifications from '@/composables/useNotifications'
import { permissionScope, hasPermission } from '@/composables/usePermission'
import { useRouter } from '@core/utils/utils'
import { ref, computed, set } from '@vue/composition-api'
import i18n from '@/libs/i18n'
import useCommon from "@/views/organization/useCommon"
import useCommonTodo from '@/views/apps/todo/useCommonTodo'
import store from '@/store'
import { flatPickrConfig } from '@/constants'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useAuditLog from '@/composables/useAuditLog'

export default function useCaseEdit() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { route, router } = useRouter()
  const { formatDate, setState } = useCommon()
  const { uploadImprovementFile, sendEmailCase } = useCommonTodo()
  const { updateItem, getItemsAggregate } = useLambda()
  const { addAuditEntry } = useAuditLog()
  const caseData = ref({})
  const isSubmitting = ref(false)
  const updateActiveTab = ref(false)
  const originalData = ref(null)
  const userData = store.state?.userStore?.userData
  const startOfToday = new Date()
  const endOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  endOfToday.setHours(23, 59, 59, 999)
  const { default_language } = JSON.parse(localStorage.getItem('clientData') || '{}')
  const query = { _id: oid(route.value.params.id) }
  const collection = 'improvement'
  const workerObjectId = oid(userData.worker_id?.$oid)

  const show = async (caseId = null) => {
    isSubmitting.value = true

    try {
      // Capa 2: usa View (esta pantalla sirve tanto a Ver como a Editar) — Editar siempre
      // hereda el scope de Ver, así que este mismo chequeo aplica a los dos casos.
      const restrictToOwn = permissionScope('case-management', 'view') === 'own'

      // "actionPlanStages" no es un campo crudo del documento — solo existe una vez resuelto
      // vía $lookup (mismo patrón que casesQueryWithOffset). Por eso este $or no puede ir en
      // el primer $match: en ese punto el documento crudo todavía no lo tiene, así que esa
      // condición nunca matcheaba y bloqueaba a cualquiera cuyo único vínculo con el caso
      // fuera ser executionResponsible de una etapa (bug encontrado probando Capa 2/3).
      const scopeMatch = restrictToOwn
        ? {
          $or: [
            { proposalResponsible: workerObjectId },
            { approvalResponsible: workerObjectId },
            { verificationResponsible: workerObjectId },
            { actionPlanStages: { $elemMatch: { executionResponsible: workerObjectId } } },
          ],
        }
        : {}

      const pipeline = [
        {
          $match: {
            ...(caseId ? { _id: oid(caseId) } : query),
            client_id: oid(userData.client.$oid),
            deleted: { $ne: true },
          },
        },
        // Resuelve actionPlanStages ANTES del $match de scope de abajo, así "soy
        // executionResponsible de alguna etapa" es evaluable — mismo orden que ya funciona
        // en casesQueryWithOffset (useMongoDBFunctions.js).
        {
          $lookup: {
            from: 'action_plan',
            localField: 'actionPlans',
            foreignField: '_id',
            pipeline: [ { $project: { _id: 0, executionResponsible: 1 } } ],
            as: 'actionPlanStages',
          },
        },
        { $match: scopeMatch },
        // Ya cumplió su función (evaluar el scope) — se saca para no dejarlo colgando en el
        // documento final; la vista usa el `actionPlans` resuelto más abajo, con más detalle.
        { $unset: 'actionPlanStages' },
        {
          $lookup: {
            from: 'worker',
            localField: 'assignee',
            foreignField: '_id',
            pipeline: [ { $project: { name: 1, email: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
            as: 'assignee'
          }
        },
        {
          $lookup: {
            from: 'worker',
            localField: 'proposalResponsible',
            foreignField: '_id',
            pipeline: [ { $project: { name: 1, email: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
            as: 'proposalResponsible'
          }
        },
        {
          $lookup: {
            from: 'worker',
            localField: 'approvalResponsible',
            foreignField: '_id',
            pipeline: [ { $project: { name: 1, email: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
            as: 'approvalResponsible'
          }
        },
        {
          $lookup: {
            from: 'worker',
            localField: 'verificationResponsible',
            foreignField: '_id',
            pipeline: [ { $project: { name: 1, email: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
            as: 'verificationResponsible'
          }
        },
        {
          $lookup: {
            from: 'improvement',
            localField: 'associatedCases',
            foreignField: '_id',
            pipeline: [ { $project: { note: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
            as: 'associatedCases'
          }
        },
        {
          $lookup: {
            from: 'location',
            localField: 'areas',
            foreignField: '_id',
            pipeline: [ { $project: { location: 1, email: 1 } } ],
            as: 'areas'
          }
        },
        {
          $lookup: {
            from: 'company',
            localField: 'company',
            foreignField: '_id',
            pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
            as: 'company'
          }
        },
        {
          $lookup: {
            from: 'id',
            localField: 'businessActivity',
            foreignField: '_id',
            pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
            as: 'businessActivity'
          }
        },
        {
          $lookup: {
            from: 'process',
            localField: 'process',
            foreignField: '_id',
            pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
            as: 'process'
          }
        },
        {
          $lookup: {
            from: 'crime',
            localField: 'crime',
            foreignField: '_id',
            pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
            as: 'crime'
          }
        },
        {
          $lookup: {
            from: 'control',
            localField: 'control',
            foreignField: '_id',
            pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
            as: 'control'
          }
        },
        {
          $lookup: {
            from: 'worker',
            localField: 'controlResponsible',
            foreignField: '_id',
            pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
            as: 'controlResponsible'
          }
        },
        {
          $lookup: {
            from: 'action_plan',
            localField: 'actionPlans',
            foreignField: '_id',
            pipeline: [
              {
                $lookup: {
                  from: 'worker',
                  localField: 'executionResponsible',
                  foreignField: '_id',
                  pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
                  as: 'executionResponsible'
                }
              },
              {
                $lookup: {
                  from: 'worker',
                  localField: 'lastModifiedBy',
                  foreignField: '_id',
                  pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
                  as: 'lastModifiedBy'
                }
              },
              { $addFields: { _id: { $toString: "$_id" }, executionResponsible: { $arrayElemAt: ["$executionResponsible", 0] }, lastModifiedBy: { $arrayElemAt: ["$lastModifiedBy", 0] } } },
            ],
            as: 'actionPlans'
          }
        },
        { $addFields: { _id: { $toString: "$_id" }, assignee: { $arrayElemAt: ["$assignee", 0] }, proposalResponsible: { $arrayElemAt: ["$proposalResponsible", 0] }, approvalResponsible: { $arrayElemAt: ["$approvalResponsible", 0] }, verificationResponsible: { $arrayElemAt: ["$verificationResponsible", 0] } } },
      ]
    
      const items = await getItemsAggregate({ collection, pipeline })

      if (!items.length) {
        showErrorMessage(i18n.t('message.you_are_not_authorized'))
        if (!caseId) router.push({ name: 'organization-case-list' })
        return
      }

      const c = items[0] || {}

      // Sort action plans by origin index
      c.actionPlans?.sort((a, b) => a.originIndex - b.originIndex)

      // When the function comes with caseId parameter, it is because it will be used to print to PDF
      caseData.value = {
        ...c,
        assignee: caseId ? c.assignee?.name : c.assignee?._id,
        proposalResponsible: caseId ? c.proposalResponsible?.name : c.proposalResponsible?._id,
        approvalResponsible: caseId ? c.approvalResponsible?.name : c.approvalResponsible?._id,
        verificationResponsible: caseId ? c.verificationResponsible?.name : c.verificationResponsible?._id,
        associatedCases: caseId ? c.associatedCases?.map(e => e.note) || [] : c.associatedCases?.map(e => e._id) || [],
        areas: caseId ? c.areas?.map(e => e.location) || [] : c.areas?.map(e => e._id) || [],
        company: caseId ? c.company?.map(e => e.name) || [] : c.company?.map(e => e._id) || [],
        businessActivity: caseId ? c.businessActivity?.map(e => e.name) || [] : c.businessActivity?.map(e => e._id) || [],
        process: caseId ? c.process?.map(e => e.name) || [] : c.process?.map(e => e._id) || [],
        crime: caseId ? c.crime?.map(e => e.name) || [] : c.crime?.map(e => e._id) || [],
        control: caseId ? c.control?.map(e => e.name) || [] : c.control?.map(e => e._id) || [],
        controlResponsible: caseId ? c.controlResponsible?.map(e => e.name) || [] : c.controlResponsible?.map(e => e._id) || [],
        actionPlans: c.actionPlans?.map(e => ({
          ...e,
          executionResponsible: caseId ? e.executionResponsible?.name : e.executionResponsible?._id,
          startDateConfig: {
            ...flatPickrConfig,
            maxDate: e.type === 'immediate' ? endOfToday : null,
          },
          endDateConfig: {
            ...flatPickrConfig,
            minDate: e.startDate || null,
          },
          lastModifiedDate: e.lastModifiedDate ? `${(new Date(e.lastModifiedDate)).toLocaleDateString(`${default_language || 'en'}-US`, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}hs` : null,
        })) || [],
        state: setState(c, startOfToday)
      }

      // Set reactive properties
      caseData.value.actionPlans.forEach(e => {
        set(e, 'approvalResult', e.approvalResult)
        set(e, 'correctiveResult', e.correctiveResult)
        set(e, 'verificationResult', e.verificationResult)
        set(e, 'approvalNextStage', e.stage === 'analysis' || e.stage === 'corrective' || e.stage === 'verification' ? e.stage : '')
        set(e, 'correctiveNextStage', e.stage === 'analysis' || e.stage === 'verification' ? e.stage : '')
        set(e, 'verificationNextStage', e.stage === 'analysis' || e.stage === 'corrective' || e.stage === 'end' ? e.stage : '')
        set(e, 'evidenceFile', [])
      })

      // Inicializar history si no existe (documentos legacy)
      if (!items[0].history) {
        items[0].history = []
        c.history = []
      }

      originalData.value = JSON.parse(JSON.stringify(items[0]))
      updateActiveTab.value = true
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.case_fetch_error'))
    } finally {
      isSubmitting.value = false
    }
  }

  const update = async (caseData) => {
    if (!hasPermission('case-management', 'edit')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }

    if (caseData.actionPlansToDelete?.length) deleteActionPlans(caseData.actionPlansToDelete)

    try {
      const payload = {
        note: caseData.note,
        assignee: oid(caseData.assignee),
        dueDate: date(formatDate(caseData.dueDate)),
        description: caseData.description || '',
        associatedCases: caseData.associatedCases ? caseData.associatedCases.map(e => oid(e)) : null,
        areas: caseData.areas ? caseData.areas.map(e => oid(e)) : null,
        company: caseData.company ? caseData.company.map(e => oid(e)) : [],
        businessActivity: caseData.businessActivity ? caseData.businessActivity.map(e => oid(e)) : [],
        process: caseData.process ? caseData.process.map(e => oid(e)) : [],
        crime: caseData.crime ? caseData.crime.map(e => oid(e)) : [],
        control: caseData.control ? caseData.control.map(e => oid(e)) : [],
        controlResponsible: caseData.controlResponsible ? caseData.controlResponsible.map(e => oid(e)) : [],
        proposalResponsible: oid(caseData.assignee),
        approvalResponsible: caseData.approvalResponsible ? oid(caseData.approvalResponsible) : null,
        verificationResponsible: caseData.verificationResponsible ? oid(caseData.verificationResponsible) : null,
        proposalDueDate: caseData.proposalDueDate ? date(formatDate(caseData.proposalDueDate)) : null,
        approvalDueDate: caseData.approvalDueDate ? date(formatDate(caseData.approvalDueDate)) : null,
        verificationDueDate: caseData.verificationDueDate ? date(formatDate(caseData.verificationDueDate)) : null,
        problemStatement: caseData.problemStatement || '',
        why1: caseData.why1 || '',
        why2: caseData.why2 || '',
        why3: caseData.why3 || '',
        why4: caseData.why4 || '',
        why5: caseData.why5 || '',
        actionPlans: caseData.actionPlansIds?.map(e => oid(e)) || [],
        approvalComment: caseData.approvalComment || '',
        verificationComment: caseData.verificationComment || '',
        lastModifiedBy: userData?.worker_id?.$oid ? oid(userData.worker_id.$oid) : null,
        rootCause: caseData.rootCause || 100,
      }

      // If case is in 'analysis' stage and there are action plans then set stage as 'approval'
      // If case is in 'approval' stage and all action plans are in stage 'corrective/verification' then set stage as 'corrective'
      // If case is in 'corrective' stage and all action plans are in stage 'verification' then set stage as 'verification'
      // If case is in 'verification' stage and all action plans are in stage 'end' then set stage as 'end'
      payload.stage = caseData.stage === 'analysis' && caseData.actionPlansIds?.length
        ? 'approval'
        : caseData.stage === 'approval' && caseData.actionPlans.some(e => e.stage === 'corrective' || e.approvalNextStage === 'corrective' || e.stage === 'verification' || e.approvalNextStage === 'verification')
          ? caseData.actionPlans.some(e => e.stage === 'verification' || e.approvalNextStage === 'verification')
            ? 'verification'
            : 'corrective'
          : caseData.stage === 'corrective' && caseData.actionPlans.some(e => e.stage === 'verification' || e.correctiveNextStage === 'verification')
            ? 'verification'
            : caseData.stage === 'verification' && caseData.actionPlans.every(e => e.verificationNextStage === 'end')
              ? 'end'
              : caseData.stage

      // If case is being set to 'end' check if it is completed overdue
      if (caseData.stage === 'verification' && caseData.actionPlans.every(e => e.verificationNextStage === 'end') && payload.verificationDueDate < startOfToday) {
        payload.isCompletedOverdue = true
      }

      const action = { $set: payload, $push: { allFileKeys: { $each: caseData.newEvidenceKeys || [] } } }

      await updateItem({ collection, query, action })

      // Add audit log entry
      await addAuditEntry({
        collection,
        documentId: oid(route.value.params.id),
        originalData: originalData.value,
        updatedData: payload,
      })

      showSuccessMessage(i18n.t('message.improvement_updated'))

      // Upload document to AWS and then update the case in MongoDB with the AWS document key
      if (caseData.documentFiles?.length) {
        uploadImprovementFile(caseData.documentFiles, "documents", route.value.params.id)
      }

      const originalStage = caseData.stage
      caseData.stage = payload.stage

      // Send email to assignee (registration responsible) with the details of the case updated
      sendEmailCase(caseData, "update", 'assignee', 'case')

      // If case passed to stage 'approval' send email to approval responsible with the details of the case updated
      if (caseData.approvalResponsible && originalStage !== 'approval' && payload.stage === 'approval') {
        sendEmailCase(caseData, "update", 'approvalResponsible', 'case')
      }

      // If case passed to stage 'verification' send email to verification responsible with the details of the case updated
      else if (caseData.verificationResponsible && originalStage !== 'verification' && payload.stage === 'verification') {
        sendEmailCase(caseData, "update", 'verificationResponsible', 'case')
      }
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.improvement_update_error'))
    } finally {
      router.push({ name: 'organization-case-list' })
    }
  }

  const deleteActionPlans = (ids) => {
    ids.forEach(async (e) => {
      try {
        const query = { _id: oid(e) }
        const payload = { deleted: true }
        const action = { $set: payload }

        await updateItem({ collection: 'action_plan', query, action })
      } catch (error) {
        console.log(error)
      }
    })
  }

  return {
    update,
    show,
    caseData,
    isSubmitting,
    updateActiveTab,
  }
}
